// CustomisableHome — renders the user's saved Home-tab layout and provides
// an in-place "Customise" mode (drag-to-reorder, hide/show, size cycle) plus
// a right-side drawer to add hidden widgets back. Single default dashboard
// per user — multiple named dashboards is a future enhancement.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import logger from '@/lib/logger';
import {
  Lock, Unlock, GripVertical, Eye, EyeOff, Plus, X,
  RotateCcw, Maximize2, LayoutGrid, Sparkles,
} from 'lucide-react';
import { WIDGET_RENDERERS, SIDE_BY_SIDE_PAIRS } from './widgetRegistry';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SIZE_CYCLE = { L: 'M', M: 'S', S: 'L' };

function authHeaders(token) {
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
}

export default function CustomisableHome({ token, ctx }) {
  const [dashboardId, setDashboardId] = useState(null);
  const [layout, setLayout] = useState([]); // [{widgetId, visible, size}]
  const [catalog, setCatalog] = useState([]); // [{id,label,category,description,defaultSize}]
  const [editing, setEditing] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const dragIndex = useRef(null);
  const saveTimer = useRef(null);

  // ---- load ---------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [dashRes, catRes] = await Promise.all([
          fetch(`${API}/user-dashboards`, { headers: authHeaders(token) }),
          fetch(`${API}/user-dashboards/widgets`, { headers: authHeaders(token) }),
        ]);
        if (!dashRes.ok || !catRes.ok) throw new Error('Dashboard load failed');
        const dashData = await dashRes.json();
        const catData = await catRes.json();
        if (cancelled) return;
        const defaultDash =
          dashData.items.find((d) => d.id === dashData.defaultId) || dashData.items[0];
        setDashboardId(defaultDash?.id || null);
        setLayout(defaultDash?.layout || []);
        setCatalog(catData.widgets || []);
      } catch (e) {
        logger.warn('CustomisableHome: load failed', e);
        toast.error('Could not load your dashboard layout');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const catalogById = useMemo(() => {
    const m = {};
    catalog.forEach((w) => { m[w.id] = w; });
    return m;
  }, [catalog]);

  // ---- persist (debounced) -----------------------------------------------
  const scheduleSave = useCallback((nextLayout) => {
    if (!dashboardId) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        setSaving(true);
        const res = await fetch(`${API}/user-dashboards/${dashboardId}`, {
          method: 'PATCH',
          headers: authHeaders(token),
          body: JSON.stringify({ layout: nextLayout }),
        });
        if (!res.ok) throw new Error('save failed');
      } catch (e) {
        logger.warn('CustomisableHome: save failed', e);
        toast.error('Could not save layout changes');
      } finally {
        setSaving(false);
      }
    }, 500);
  }, [dashboardId, token]);

  const updateLayout = useCallback((nextLayout) => {
    setLayout(nextLayout);
    scheduleSave(nextLayout);
  }, [scheduleSave]);

  // ---- layout mutations ---------------------------------------------------
  const toggleVisible = (widgetId) => {
    updateLayout(layout.map((item) =>
      item.widgetId === widgetId ? { ...item, visible: !item.visible } : item
    ));
  };

  const cycleSize = (widgetId) => {
    updateLayout(layout.map((item) =>
      item.widgetId === widgetId ? { ...item, size: SIZE_CYCLE[item.size] || 'L' } : item
    ));
  };

  const removeWidget = (widgetId) => {
    // "Remove" from layout entirely — falls back to drawer for re-adding
    updateLayout(layout.filter((item) => item.widgetId !== widgetId));
  };

  const addWidget = (widgetId) => {
    if (layout.some((item) => item.widgetId === widgetId)) {
      // already present — just make it visible
      updateLayout(layout.map((item) =>
        item.widgetId === widgetId ? { ...item, visible: true } : item
      ));
      return;
    }
    const w = catalogById[widgetId];
    updateLayout([
      ...layout,
      { widgetId, visible: true, size: w?.defaultSize || 'L' },
    ]);
    toast.success(`Added "${w?.label || widgetId}"`);
  };

  const resetToDefault = async () => {
    if (!dashboardId) return;
    try {
      setSaving(true);
      const res = await fetch(`${API}/user-dashboards/${dashboardId}/reset-to-recommended`, {
        method: 'POST',
        headers: authHeaders(token),
      });
      if (!res.ok) throw new Error('reset failed');
      const body = await res.json();
      const nextLayout = body?.dashboard?.layout || [];
      setLayout(nextLayout);
      const orgLabel = { sfo: 'Single-Family-Office', advice: 'Advice-firm', mfo: 'Multi-Family-Office' }[body.orgType] || 'recommended';
      toast.success(`Layout reset to the ${orgLabel} default`);
    } catch (e) {
      logger.warn('CustomisableHome: reset failed', e);
      toast.error('Could not reset layout');
    } finally {
      setSaving(false);
    }
  };

  // ---- drag-and-drop ------------------------------------------------------
  const onDragStart = (i) => (e) => {
    dragIndex.current = i;
    e.dataTransfer.effectAllowed = 'move';
    // Firefox requires data to be set
    try { e.dataTransfer.setData('text/plain', String(i)); } catch (_) { /* noop */ }
  };
  const onDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  const onDrop = (target) => (e) => {
    e.preventDefault();
    const from = dragIndex.current;
    dragIndex.current = null;
    if (from === null || from === target) return;
    const next = [...layout];
    const [moved] = next.splice(from, 1);
    next.splice(target, 0, moved);
    updateLayout(next);
  };

  // ---- render -------------------------------------------------------------
  if (loading) {
    return (
      <div className="space-y-4" data-testid="customisable-home-loading">
        <div className="h-24 rounded-lg bg-[#F5F3F1] animate-pulse" />
        <div className="h-48 rounded-lg bg-[#F5F3F1] animate-pulse" />
        <div className="h-48 rounded-lg bg-[#F5F3F1] animate-pulse" />
      </div>
    );
  }

  // Determine which widgets in `layout` get rendered, and group consecutive
  // M-sized items from SIDE_BY_SIDE_PAIRS into the original two-col grid so
  // the out-of-the-box visual matches the previous Home tab exactly.
  const visibleItems = layout.filter((it) => editing || it.visible);
  const renderQueue = [];
  let i = 0;
  while (i < visibleItems.length) {
    const current = visibleItems[i];
    const next = visibleItems[i + 1];
    const pair = SIDE_BY_SIDE_PAIRS.find(
      (p) => p[0] === current.widgetId && p[1] === next?.widgetId
    );
    if (!editing && pair && current.size === 'M' && next.size === 'M' && current.visible && next.visible) {
      renderQueue.push({ kind: 'pair', items: [current, next], indices: [i, i + 1] });
      i += 2;
    } else {
      renderQueue.push({ kind: 'single', item: current, index: i });
      i += 1;
    }
  }

  const hiddenOrMissing = catalog.filter((w) => {
    const inLayout = layout.find((it) => it.widgetId === w.id);
    return !inLayout || !inLayout.visible;
  });

  return (
    <div className="relative">
      {/* Customise toolbar */}
      <div
        className="flex flex-wrap items-center justify-between gap-3 mb-6 -mt-2"
        data-testid="customise-toolbar"
      >
        <div className="flex items-center gap-2 text-sm text-[#5D524D]">
          <LayoutGrid className="w-4 h-4" />
          <span className="font-medium">My Home</span>
          {editing && (
            <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-[#4A3B32] text-white" data-testid="editing-pill">
              Customise mode
            </span>
          )}
          {saving && (
            <span className="ml-2 text-xs text-[#8C8078]" data-testid="saving-indicator">
              Saving…
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {editing && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={resetToDefault}
                className="text-[#5D524D] hover:text-[#2C2420]"
                data-testid="reset-layout-btn"
                title="Reset to the recommended layout for your organisation type"
              >
                <RotateCcw className="w-4 h-4 mr-1.5" />
                Use recommended layout
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setDrawerOpen(true)}
                className="text-[#5D524D] hover:text-[#2C2420]"
                data-testid="open-widget-tray-btn"
              >
                <Plus className="w-4 h-4 mr-1.5" />
                Add widget
              </Button>
            </>
          )}
          <Button
            variant={editing ? 'default' : 'outline'}
            size="sm"
            onClick={() => setEditing((v) => !v)}
            data-testid={editing ? 'done-customising-btn' : 'customise-home-btn'}
            className={editing
              ? 'bg-[#4A3B32] hover:bg-[#3a2d24] text-white'
              : 'border-[#E6E0D9] text-[#2C2420] hover:bg-[#F5F3F0]'}
          >
            {editing ? (
              <><Lock className="w-4 h-4 mr-1.5" />Done</>
            ) : (
              <><Unlock className="w-4 h-4 mr-1.5" />Customise</>
            )}
          </Button>
        </div>
      </div>

      {/* Widgets */}
      <div className="space-y-6">
        {renderQueue.length === 0 && (
          <div className="text-center py-10 border border-dashed border-[#E6E0D9] rounded-xl" data-testid="empty-layout">
            <Sparkles className="w-6 h-6 mx-auto text-[#8C8078] mb-2" />
            <p className="text-sm text-[#5D524D]">Your dashboard is empty.</p>
            <Button
              variant="outline"
              size="sm"
              className="mt-3"
              onClick={() => { setEditing(true); setDrawerOpen(true); }}
              data-testid="empty-layout-add-btn"
            >
              <Plus className="w-4 h-4 mr-1.5" />Add widgets
            </Button>
          </div>
        )}
        {renderQueue.map((entry, idx) =>
          entry.kind === 'pair' ? (
            <div key={`pair-${entry.items[0].widgetId}-${entry.items[1].widgetId}`} className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {entry.items.map((item, j) => (
                <WidgetShell
                  key={item.widgetId}
                  item={item}
                  meta={catalogById[item.widgetId]}
                  editing={editing}
                  onToggleVisible={toggleVisible}
                  onCycleSize={cycleSize}
                  onRemove={removeWidget}
                  onDragStart={onDragStart(entry.indices[j])}
                  onDragOver={onDragOver}
                  onDrop={onDrop(entry.indices[j])}
                  ctx={ctx}
                />
              ))}
            </div>
          ) : (
            <WidgetShell
              key={entry.item.widgetId}
              item={entry.item}
              meta={catalogById[entry.item.widgetId]}
              editing={editing}
              onToggleVisible={toggleVisible}
              onCycleSize={cycleSize}
              onRemove={removeWidget}
              onDragStart={onDragStart(entry.index)}
              onDragOver={onDragOver}
              onDrop={onDrop(entry.index)}
              ctx={ctx}
            />
          )
        )}
      </div>

      {/* Widget Tray (right-side drawer) */}
      {drawerOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/30"
            onClick={() => setDrawerOpen(false)}
            data-testid="widget-tray-overlay"
          />
          <aside
            className="fixed top-0 right-0 bottom-0 w-full sm:w-[400px] z-50 bg-white shadow-2xl border-l border-[#E6E0D9] flex flex-col"
            data-testid="widget-tray"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#E6E0D9]">
              <div>
                <h3 className="font-heading text-lg text-[#2C2420]">Widget tray</h3>
                <p className="text-xs text-[#5D524D]">Hidden or removed widgets ready to add back.</p>
              </div>
              <button
                onClick={() => setDrawerOpen(false)}
                className="p-1.5 rounded-md hover:bg-[#F5F3F0] text-[#5D524D]"
                data-testid="close-widget-tray-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {hiddenOrMissing.length === 0 ? (
                <div className="text-sm text-[#5D524D] text-center py-10" data-testid="widget-tray-empty">
                  Every widget is on your dashboard already.
                </div>
              ) : (
                hiddenOrMissing.map((w) => (
                  <div
                    key={w.id}
                    className="p-3 rounded-lg border border-[#E6E0D9] hover:border-[#4A3B32]/30 transition-colors"
                    data-testid={`tray-widget-${w.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-[#2C2420] truncate">{w.label}</p>
                        <p className="text-xs text-[#5D524D] mt-0.5">{w.description}</p>
                        <span className="inline-block mt-1.5 text-[10px] uppercase tracking-wide text-[#8C8078]">{w.category}</span>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => addWidget(w.id)}
                        className="shrink-0"
                        data-testid={`add-widget-${w.id}-btn`}
                      >
                        <Plus className="w-4 h-4 mr-1" />Add
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </aside>
        </>
      )}
    </div>
  );
}

function WidgetShell({
  item, meta, editing, onToggleVisible, onCycleSize, onRemove,
  onDragStart, onDragOver, onDrop, ctx,
}) {
  const renderer = WIDGET_RENDERERS[item.widgetId];
  if (!renderer) return null;
  if (!editing && !item.visible) return null;

  if (!editing) {
    return <div data-testid={`widget-${item.widgetId}`}>{renderer(ctx)}</div>;
  }

  return (
    <div
      className={cn(
        'relative rounded-xl border-2 border-dashed transition-all',
        item.visible ? 'border-[#4A3B32]/40 bg-white/60' : 'border-[#E6E0D9] bg-[#F5F3F1]/60 opacity-60'
      )}
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      data-testid={`widget-edit-${item.widgetId}`}
    >
      {/* Edit chrome */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-dashed border-[#E6E0D9] bg-white/80 rounded-t-xl">
        <div className="flex items-center gap-2 min-w-0">
          <button
            className="cursor-grab active:cursor-grabbing text-[#8C8078] hover:text-[#2C2420]"
            title="Drag to reorder"
            data-testid={`drag-${item.widgetId}`}
          >
            <GripVertical className="w-4 h-4" />
          </button>
          <span className="text-sm font-medium text-[#2C2420] truncate">{meta?.label || item.widgetId}</span>
          <span className="text-[10px] uppercase tracking-wide text-[#8C8078] shrink-0">{item.size}</span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={() => onCycleSize(item.widgetId)}
            className="p-1 rounded hover:bg-[#F5F3F0] text-[#5D524D]"
            title="Cycle size (L → M → S)"
            data-testid={`cycle-size-${item.widgetId}`}
          >
            <Maximize2 className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => onToggleVisible(item.widgetId)}
            className="p-1 rounded hover:bg-[#F5F3F0] text-[#5D524D]"
            title={item.visible ? 'Hide widget' : 'Show widget'}
            data-testid={`toggle-visible-${item.widgetId}`}
          >
            {item.visible ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
          </button>
          <button
            onClick={() => onRemove(item.widgetId)}
            className="p-1 rounded hover:bg-red-50 text-[#5D524D] hover:text-red-600"
            title="Remove from dashboard"
            data-testid={`remove-${item.widgetId}`}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      <div className={cn('p-3', !item.visible && 'pointer-events-none')}>
        {renderer(ctx)}
      </div>
    </div>
  );
}
